import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { ApiService } from "services/ApiService";

interface FileUploadProps {
    onUploadSuccess: () => void; // Callback prop
}

const FileUpload: React.FC<FileUploadProps> = ({ onUploadSuccess }) => {
    const [file, setFile] = useState<File | null>(null);
    const [preview, setPreview] = useState<string | null>(null);
    const { artistId } = useParams<{ artistId: string }>();

    if (!artistId) return null;

    // Handle file selection
    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = event.target.files?.[0];
        if (!selectedFile) return;

        setFile(selectedFile);

        // Preview image if it's an image file
        if (selectedFile.type.startsWith("image/")) {
            const reader = new FileReader();
            reader.onloadend = () => setPreview(reader.result as string);
            reader.readAsDataURL(selectedFile);
        }
    };

    // Simulated file upload function
    const handleUpload = async () => {
        if (!file) {
            alert("Please select a file first!");
            return;
        }

        const formData = new FormData();
        formData.append("file", file);

        try {
            const response = await ApiService.uploadArtistImage(artistId, formData);

            if (response.success) {
                alert("File uploaded successfully!");
                onUploadSuccess(); // Trigger re-render in parent
            } else {
                alert("File upload failed.");
            }
        } catch (error) {
            console.error("Error uploading file:", error);
            alert("Error uploading file.");
        }
    };

    return (
        <div className="text-center p-6 bg-gray-100 rounded-lg shadow-md">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Upload a File</h2>

            {/* File Input */}
            <input
                type="file"
                onChange={handleFileChange}
                className="block mx-auto mb-4 text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />

            {/* Image Preview */}
            {preview && (
                <div className="mt-4">
                    <p className="text-gray-600 mb-2">Preview:</p>
                    <img
                        src={preview}
                        alt="File Preview"
                        className="mx-auto max-w-xs rounded-lg shadow-md"
                    />
                </div>
            )}

            {/* Upload Button */}
            <button
                onClick={handleUpload}
                className="mt-4 px-6 py-2 bg-green-500 text-white font-semibold rounded-lg shadow-md hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-400 focus:ring-offset-2"
            >
                Upload File
            </button>
        </div>
    );
};

export default FileUpload;